import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchSqlData, fetchSqlDataWithQuery } from '../services/api';
import type {
  CorCountries,  Company,  CorCurrencies,  CorProfessions,  CorFoodIngredients,
  VwQaqcInspFormDetails,  AdmKitchRecipe,  VwAdmKitchRecipe,  AdmKitchRecipeGrConversion,  CorUnits,  HseMachInspSheetData,
  HseMachInspSheetDesigns,  ConstMachines,  VwConstMachines,  VwPersonnel,
  ConstMachineAllocation,  VwMachMachineReservation,  VwHseMachInspSheetDetails,
  QaqcInspFormData,  QaqcInspFormDesigns,  VwQaqcInspRecord,  VwHseMachInspRecords,
  ProjSystems,  VwProjSubsystems,  QaqcRfiForms,  QaqcPipeRfiForms,  ItUsers,
  ItLogsTkfAdmin,  ItUserLoginLog,  CorFormGroup,  CorDestinationFolder,  VwTkfWbsHierarchy,
  PrimaveraTask,  PrimaveraTaskSmall,  VwTkfTaskResources,  VwTkfResourceSummary,  VwTkfTaskActivityCode,
  VwTkfTaskActivityCodeTaskIdOnly,  MatGapMaster,  MatWarehouses,  MatGapDeliveries,
  MatGapDeliveriesTemporary,  MatGapTxn,  VwMatGapWhInventory,  VwMatGapIssueRequestDetails,
  MatGapIssueRequest,  VwMatGapRequestItemOutstanding,  ToPlnCostCenters,  VwToContractDetails,
  HseWorkPermit,  HseTeam,  HseTeamMember,  VwHseTeamsFull,  CorTodo,  QaqcPipeMaterialTypes,
  VwQaqcPipeMaterials,  QaqcPipeServiceClass,  QaqcPipeWeldingRod,  QaqcPipeWelder,
  QaqcPipeWps,  QaqcPipeInspector,  QaqcPipeWorkarea,  QaqcPipeWeldtype,  QaqcPipePipesize,
  QaqcPipeHeatno,  QaqcPipeWeldingdata,  QaqcPipeModificationCode,  QaqcPipeWeldingdataResult,
  QaqcPipeWeldingdataLog,  VwQaqcPipeNdtPerformance,  VwQaqcPipeNdtPerformanceByKind,  QaqcPipeInspRcrdDetail,
  VwQaqcPipeReportBc,  VwQaqcPipeReportVc,  MatClntDelivery,  VwMatClntMatPipingItem,  MatClntIssueRequest,
  MatClntIssueRequestItem,  VwMatClntWhInventory,  MatClntMaster,
  VwMatClntRequestItemOutstanding,  MatClntPipingBom,
  MatLogisticsMppl,  VwCorUneceUnlocodesBasic,  MvUnlocodeCountry,  MatLogInvoiceLine,
  MatLogInvoiceSummary,  MatLogisticShipment,  MatLogisticPackage,  MatLogisticPackageItem,
  DccOldArchive,  DccTranslationLogEnrichedRow,  ToPlnP6ProgressUpdateRow,
  TkfTaskTotalCostRow,
} from '../types';
import { useGenericFetch, useGenericStreamFetch, useSimpleStream } from './genericHookCreator';

export const useGetCountries = () => {
  return useGenericFetch<CorCountries>(
    'cor_countries',    'countries',    5 * 60 * 1000 // 5 minutes
  );
};

// Logistics: Shipments
export const useGetMatLogisticShipments = () => {
  return useGenericFetch<MatLogisticShipment>(
    'mat_logistic_shipments',
    'mat_logistic_shipments',
    5 * 60 * 1000, // 5 minutes
    undefined,
    true,          // refetch on focus to stay fresh
    true,          // event refresh enabled if backend emits
    'mat_logistic_shipments:changed'
  );
};

// Logistics: Packages
export const useGetMatLogisticPackages = () => {
  return useGenericFetch<MatLogisticPackage>(
    'mat_logistic_packages',
    'mat_logistic_packages',
    5 * 60 * 1000, // 5 minutes
    undefined,
    true,          // refetch on focus
    true,          // event refresh
    'mat_logistic_packages:changed'
  );
};

// Logistics: Package Items
export const useGetMatLogisticPackageItems = () => {
  return useGenericFetch<MatLogisticPackageItem>(
    'mat_logistic_package_items',
    'mat_logistic_package_items',
    5 * 60 * 1000, // 5 minutes
    undefined,
    true,          // refetch on focus
    true,          // event refresh
    'mat_logistic_package_items:changed'
  );
};

export const useGetMatLogisticPackageItemsByPackageId = (package_id?: string | null) => {
  return useQuery<{ success?: boolean; data?: MatLogisticPackageItem[] } | MatLogisticPackageItem[] | any>({
    queryKey: ['mat_logistic_package_items', package_id],
    enabled: !!package_id,
    queryFn: async () => {
      const pid = String(package_id).replace(/'/g, "''");
      const sql = `select * from public.mat_logistic_package_items where package_id = '${pid}' order by item_id nulls last, id`;
      return fetchSqlDataWithQuery(sql);
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetTkfTasksTotalCosts = (params?: {
  proj_id?: number | string | null;
  task_id?: number | string | null;
  task_code_like?: string | null;
}) => {
  const projId = params?.proj_id != null && String(params.proj_id).trim() !== '' ? Number(params.proj_id) : null;
  const taskId = params?.task_id != null && String(params.task_id).trim() !== '' ? Number(params.task_id) : null;
  const taskCodeLike = params?.task_code_like != null && String(params.task_code_like).trim() !== ''
    ? String(params.task_code_like).trim()
    : null;

  const base = useGenericFetch<TkfTaskTotalCostRow>(
    'vw_TKF_tasks_total_costs',
    'vw_TKF_tasks_total_costs:v2',
    30 * 1000,
    'PPMTRAIN',
    false,
    false,
    undefined,
    undefined,
    true
  );

  const filtered = React.useMemo(() => {
    const raw = base.data?.data ?? [];
    const pid = projId != null && Number.isFinite(projId) ? Number(projId) : null;
    const tid = taskId != null && Number.isFinite(taskId) ? Number(taskId) : null;
    const like = taskCodeLike != null ? taskCodeLike.toLowerCase() : null;

    const out = (raw || []).filter((r) => {
      if (pid != null && Number((r as any)?.proj_id) !== pid) return false;
      if (tid != null && Number((r as any)?.task_id) !== tid) return false;
      if (like != null) {
        const tc = String((r as any)?.task_code ?? '').toLowerCase();
        if (!tc.includes(like)) return false;
      }
      return true;
    });

    return { data: out };
  }, [base.data, projId, taskCodeLike, taskId]);

  return {
    ...base,
    data: filtered,
  };
};

export const useGetToPlnP6ProgressUpdatesBetweenDates = (startIso?: string | null, endIso?: string | null) => {
  const s = startIso != null && String(startIso).trim() !== '' ? String(startIso).trim() : null;
  const e = endIso != null && String(endIso).trim() !== '' ? String(endIso).trim() : null;

  return useQuery<{ success?: boolean; data?: ToPlnP6ProgressUpdateRow[] } | ToPlnP6ProgressUpdateRow[] | any>({
    queryKey: ['to_pln_p6_progress_updates', 'between_dates', s, e],
    enabled: s != null && e != null,
    queryFn: async () => {
      const start = String(s).replace(/'/g, "''");
      const end = String(e).replace(/'/g, "''");
      const sql = `
        SELECT
          id,
          update_date,
          wp_ref_num,
          activity_code,
          activity_name,
          rsrc_short_name,
          rsrc_unit_name,
          provider_code,
          provider_name,
          reported_qty,
          remarks,
          created_by,
          created_at
        FROM public.to_pln_p6_progress_updates
        WHERE update_date >= '${start}'
          AND update_date <= '${end}'
        ORDER BY update_date DESC, id DESC;
      `;
      return fetchSqlDataWithQuery(sql);
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetMatLogisticPackageShipmentEventsByPackageId = (package_id?: string | null) => {
  return useQuery<any>({
    queryKey: ['mat_logistic_package_shipment_events', package_id],
    enabled: !!package_id,
    queryFn: async () => {
      const pid = String(package_id).replace(/'/g, "''");
      const sql = `select * from public.mat_logistic_package_shipment_events where package_id = '${pid}' order by event_at nulls last, id`;
      return fetchSqlDataWithQuery(sql);
    },
    staleTime: 30 * 1000,
  });
};

export const useGetMatLogInvoiceSummary = () => {
  return useQuery<{ success?: boolean; data?: MatLogInvoiceSummary[] } | MatLogInvoiceSummary[] | any>({
    queryKey: ['vw_mat_log_invoice_summary'],
    queryFn: async () => {
      const sql = `select * from public.vw_mat_log_invoice_summary order by shipment_id, invoice_no, currency`;
      return fetchSqlDataWithQuery(sql);
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

export const useGetMatLogInvoiceLines = (params?: {
  shipment_id?: string | null;
  invoice_no?: string | null;
  currency?: string | null;
}) => {
  const shipmentId = params?.shipment_id ?? null;
  const invoiceNo = params?.invoice_no ?? null;
  const currency = params?.currency ?? null;

  return useQuery<{ success?: boolean; data?: MatLogInvoiceLine[] } | MatLogInvoiceLine[] | any>({
    queryKey: ['vw_mat_log_invoice_lines', shipmentId, invoiceNo, currency],
    enabled: !!shipmentId,
    queryFn: async () => {
      const sid = String(shipmentId).replace(/'/g, "''");
      const inv = invoiceNo != null ? String(invoiceNo).trim().replace(/'/g, "''") : '';
      const cur = currency != null ? String(currency).trim().replace(/'/g, "''") : '';

      const unwrap = (resp: any): any[] => {
        if (Array.isArray(resp)) return resp;
        if (Array.isArray(resp?.data)) return resp.data;
        if (Array.isArray(resp?.data?.data)) return resp.data.data;
        return [];
      };

      const where: string[] = [`btrim(shipment_id) = btrim('${sid}')`];
      if (inv !== '') where.push(`btrim(coalesce(invoice_no, '')) = '${inv}'`);
      if (cur !== '') where.push(`btrim(coalesce(currency, '')) = '${cur}'`);

      const sql1 = `select * from public.vw_mat_log_invoice_lines where ${where.join(' and ')} order by itemcode nulls last, unit_price nulls last, currency nulls last`;
      const resp1 = await fetchSqlDataWithQuery(sql1);
      const rows1 = unwrap(resp1);

      const hasNarrowFilters = inv !== '' || cur !== '';
      if (hasNarrowFilters && (!rows1 || rows1.length === 0)) {
        const sql2 = `select * from public.vw_mat_log_invoice_lines where btrim(shipment_id) = btrim('${sid}') order by itemcode nulls last, unit_price nulls last, currency nulls last`;
        return fetchSqlDataWithQuery(sql2);
      }

      return resp1;
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

// UN/LOCODE Countries (materialized view) - small, persist 7 days
export const useGetUnlocodeCountries = () => {
  return useGenericFetch<MvUnlocodeCountry>(
    'mv_unlocode_countries', // materialized view name
    'mv_unlocode_countries', // query key
    Infinity,                // staleTime
    undefined,               // database
    false,                   // refetch on focus
    false,                   // event refresh
    undefined,               // event name
    { persist: true, cacheOnce: true, cacheVersion: 'v1', gcDays: 7 }
  );
};

// UNECE UN/LOCODE basic materialized view with function flags (persist 7 days)
export const useGetVwCorUneceUnlocodesBasic = () => {
  return useGenericFetch<VwCorUneceUnlocodesBasic>(
    'mv_cor_unece_unlocodes_basic', // view name
    'mv_cor_unece_unlocodes_basic', // query key
    Infinity,                       // staleTime
    undefined,                      // database
    false,                          // refetch on focus
    false,                          // event refresh
    undefined                       // event name
  );
};

export const useGetDccTranslationLogEnriched = () => {
  return useGenericFetch<DccTranslationLogEnrichedRow>(
    'vw_dcc_translation_log_enriched',
    'vw_dcc_translation_log_enriched',
    30 * 1000
  );
};

// Streaming variant for Welding Data Result (large dataset)
export const useStreamQaqcPipeWeldingdataResult = (
  enabled: boolean = false,
  opts?: { orderBy?: string | string[]; columns?: string[]; batchSize?: number; flushMs?: number; stallMs?: number; eventRefresh?: boolean; eventName?: string }
) => {
  return useGenericStreamFetch<QaqcPipeWeldingdataResult>(
    'qaqc_pipe_weldingdata_result',
    'qaqc_pipe_weldingdata_result_stream',
    enabled,
    {
      orderBy: opts?.orderBy ?? ['weldingdata_id ASC', 'weld_no_rev ASC'],
      columns: opts?.columns,
      batchSize: typeof opts?.batchSize === 'number' ? opts!.batchSize : 20000,
      flushMs: typeof opts?.flushMs === 'number' ? opts!.flushMs : 120,
      stallMs: opts?.stallMs,
      eventRefresh: opts?.eventRefresh,
      eventName: opts?.eventName,
    }
  );
};

// QAQC Piping: Inspection Record Details (per-weld JSONB payload rows)
export const useGetQaqcPipeInspRecordDetails = (report_id?: string) => {
  return useQuery<{ success?: boolean; data?: QaqcPipeInspRcrdDetail[] } | QaqcPipeInspRcrdDetail[] | any>({
    queryKey: ['qaqc_pipe_insp_rcrd_details', report_id],
    enabled: !!report_id,
    queryFn: async () => {
      const rid = String(report_id).replace(/'/g, "''");
      const sql = `select * from public.qaqc_pipe_insp_rcrd_details where inspection_report_id = '${rid}' order by weldingdata_id, weld_no_rev`;
      return fetchSqlDataWithQuery(sql);
    },
    staleTime: 5 * 60 * 1000,
  });
};

// QAQC Piping: Weld Types
export const useGetQaqcPipeWeldtype = () => {
  return useGenericFetch<QaqcPipeWeldtype>(
    'qaqc_pipe_weldtype',
    'qaqc_pipe_weldtype',
    Infinity,
    undefined,
    false,
    true,
    'qaqc_pipe_weldtype:changed'
  );
};

export const useGetCompanies = () => {
  return useGenericFetch<Company>(
    'to_companies',
    'to_companies',
    5 * 60 * 1000, // 5 minutes
    undefined,
    false,
    false,
    undefined,
    { persist: true, cacheOnce: true, cacheVersion: 'v1', gcDays: 7 }
  );
};

export const useGetPersonnel = () => {
  return useGenericFetch<VwPersonnel>(
    'vw_personnel',    'vw_personnel',    5 * 60 * 1000 // 5 minutes
  );
};

export const useGetCurrencies = () => {
  return useGenericFetch<CorCurrencies>(
    'cor_currencies',    'cor_currencies',    Infinity
  );
};

export const useGetProfessions = () => {
  return useGenericFetch<CorProfessions>(
    'cor_professions',    'cor_professions',    Infinity
  );
};

export const useGetFoodIngredients = () => {
  return useGenericFetch<CorFoodIngredients>(
    'cor_food_ingredients',    'cor_food_ingredients',    Infinity
  );
};

export const useGetRecipes = () => {
  return useGenericFetch<AdmKitchRecipe>(
    'adm_kitch_recipes',    'adm_kitch_recipes',  10 * 60 * 1000 // 10 minutes
  );
};

export const useGetVwAdmKitchRecipes = () => {
  return useGenericFetch<VwAdmKitchRecipe>(
    'vw_adm_kitch_recipes', 'vw_adm_kitch_recipes', 10 * 60 * 1000 // 10 minutes
  );
};

export const useGetAdmKitchRecipeGrConversions = () => {
  return useGenericFetch<AdmKitchRecipeGrConversion>(
    'adm_kitch_recipe_gr_conversions',
    'adm_kitch_recipe_gr_conversions',
    Infinity,
    undefined,
    false,
    false,
    undefined,
    { persist: true, cacheOnce: true, cacheVersion: 'v1', gcDays: 90 }
  );
};

export const useGetUnits = () => {
  return useGenericFetch<CorUnits>(
    'cor_units',    'cor_units', Infinity
  );
};

export const useGetHseMachInspSheetData = () => {
  return useGenericFetch<HseMachInspSheetData>(
       'hse_mach_insp_sheet_data','hsemachinspsheetdata',  10 * 60 * 1000 // 10 minutes
  );
};

export const useGetHseFormDesigns = () => {
  return useGenericFetch<HseMachInspSheetDesigns>(
        'hse_mach_insp_sheet_designs', 'hseSheetDesigns', 10 * 60 * 1000 // 10 minutes
  );
};

export const useGetVwConstMachines = () => {
  return useGenericFetch<VwConstMachines>(
    'vw_const_machines',    'vw_const_machines', 10 * 60 * 1000 // 10 minutes
  );
};

export const useGetVwHseMachInspSheetDetails = () => {
  return useGenericFetch<VwHseMachInspSheetDetails>(
    'vw_hse_mach_insp_sheetdetails',    'vw_hse_mach_insp_sheetdetails', 10 * 60 * 1000 // 10 minutes
  );
};


export const useGetQaqcInspFormData = () => {
  return useGenericFetch<QaqcInspFormData>(
    'qaqc_insp_form_data','QaqcInspFormData',     10 * 60 * 1000 // 10 minutes
  );
};

export const useGetQaqcFormDesigns = () => {
  return useGenericFetch<QaqcInspFormDesigns>(
     'qaqc_insp_form_designs','QaqcInspFormDesigns',  10 * 60 * 1000 // 10 minutes
  );
};

export const useGetVwQaqcInspFormDetails = () => {
  return useGenericFetch<VwQaqcInspFormDetails>(
     'vw_qaqc_insp_formdetails','vw_qaqc_insp_formdetails',  5 * 60 * 1000 // 10 minutes
  );
};

export const useGetVwHseMachInspRecords = () => {
  return useGenericFetch<VwHseMachInspRecords>(
     'vw_hse_mach_insp_records','vw_hse_mach_insp_records',  5 * 60 * 1000 // 10 minutes
  );
};

export const useGetVwQaqcInspRecords = () => {
  return useGenericFetch<VwQaqcInspRecord>(
     'vw_qaqc_insp_records','vwQaqcInspRecords',  25 * 60 * 1000 // 10 minutes
  );
};

export const useGetProjSystems = () => {
  return useGenericFetch<ProjSystems>(
     'to_pln_proj_systems','to_pln_proj_systems',  25 * 60 * 1000 // 10 minutes
  );
};

export const useGetToPlnCostCenters = () => {
  return useGenericFetch<ToPlnCostCenters>(
    'to_pln_costcenters',
    'to_pln_costcenters',
    10 * 60 * 1000 // 10 minutes
  );
};

export const useGetContractDetails = () => {
  return useGenericFetch<VwToContractDetails>(
    'vw_to_contract_details',
    'vwContractDetails',
    10 * 60 * 1000
  );
};

export const UseGetVwProjSubsystems = () => {
  return useGenericFetch<VwProjSubsystems>(
     'vw_proj_subsystems','vwProjSubsystems',  25 * 60 * 1000 // 10 minutes
  );
};

export const UseGetQaqcRfiForms = () => {
  return useGenericFetch<QaqcRfiForms>(
     'qaqc_rfi_forms','qaqc_rfi_forms',  25 * 60 * 1000 // 10 minutes
  );
};

// QAQC Piping: Pipe RFI forms
export const useGetQaqcPipeRfiForms = () => {
  return useGenericFetch<QaqcPipeRfiForms>(
    'qaqc_pipe_rfi_forms', // table name
    'qaqc_pipe_rfi_forms', // query key
    5 * 60 * 1000,         // cache 5 minutes
    undefined,
    false,                 // no refetch on focus
    true,                  // enable event-based refresh
    'qaqc_pipe_rfi_forms:changed' // event name to listen for
  );
};

// QAQC Piping: Service Class
export const useGetQaqcPipeServiceclass = () => {
  return useGenericFetch<QaqcPipeServiceClass>(
    'qaqc_pipe_serviceclass', // table name
    'qaqc_pipe_serviceclass', // query key
    Infinity             // 10 minutes
  );
};

// QAQC Piping: Welding Rods
export const useGetQaqcPipeWeldingRod = () => {
  return useGenericFetch<QaqcPipeWeldingRod>(
    'qaqc_pipe_weldingrod', // table name
    'qaqc_pipe_weldingrod', // query key
    Infinity                // cache indefinitely
  );
};

// QAQC Piping: Welders
export const useGetQaqcPipeWelder = () => {
  return useGenericFetch<QaqcPipeWelder>(
    'qaqc_pipe_welder', // table name
    'qaqc_pipe_welder', // query key
    Infinity,           // cache indefinitely
    undefined,          // database
    false,              // enableRefetch on focus
    true,               // enableEventRefresh
    'qaqc_pipe_welder:changed' // custom event name
  );
};

// QAQC Piping: Inspectors
export const useGetQaqcPipeInspector = () => {
  return useGenericFetch<QaqcPipeInspector>(
    'qaqc_pipe_inspector', // table name
    'qaqc_pipe_inspector', // query key
    Infinity,              // cache indefinitely
    undefined,             // database
    false,                 // enableRefetch on focus
    true,                  // enableEventRefresh
    'qaqc_pipe_inspector:changed' // custom event name
  );
};

// QAQC Piping: WPS
export const useGetQaqcPipeWps = () => {
  return useGenericFetch<QaqcPipeWps>(
    'qaqc_pipe_wps', // table name
    'qaqc_pipe_wps', // query key
    Infinity,        // cache indefinitely
    undefined,       // database
    false,           // enableRefetch on focus
    true,            // enableEventRefresh
    'qaqc_pipe_wps:changed' // custom event name
  );
};

// QAQC Piping: Pipe Sizes
export const useGetQaqcPipePipesize = () => {
  return useGenericFetch<QaqcPipePipesize>(
    'qaqc_pipe_pipesize', // table name
    'qaqc_pipe_pipesize', // query key
    Infinity,             // cache indefinitely
    undefined,            // database
    false,                // enableRefetch on focus
    true,                 // enableEventRefresh
    'qaqc_pipe_pipesize:changed' // custom event name
  );
};

// QAQC Piping: Modification Codes
export const useGetQaqcPipeModificationCodes = () => {
  return useGenericFetch<QaqcPipeModificationCode>(
    'qaqc_pipe_modification_codes', // table name
    'qaqc_pipe_modification_codes', // query key
    Infinity,                       // cache indefinitely
    undefined,                      // database
    false,                          // enableRefetch on focus
    true,                           // enableEventRefresh
    'qaqc_pipe_modification_codes:changed' // custom event name
  );
};

// QAQC Piping: Heat Numbers
export const useGetQaqcPipeHeatno = () => {
  return useGenericFetch<QaqcPipeHeatno>(
    'qaqc_pipe_heatno', // table name
    'qaqc_pipe_heatno', // query key
    Infinity,           // cache indefinitely
    undefined,          // database
    false,              // enableRefetch on focus
    true,               // enableEventRefresh
    'qaqc_pipe_heatno:changed' // custom event name
  );
};

// QAQC Piping: Work Areas
export const useGetQaqcPipeWorkarea = () => {
  return useGenericFetch<QaqcPipeWorkarea>(
    'qaqc_pipe_workarea', // table name
    'qaqc_pipe_workarea', // query key
    Infinity,              // cache indefinitely
    undefined,             // database
    false,                 // enableRefetch on focus
    true,                  // enableEventRefresh
    'qaqc_pipe_workarea:changed' // custom event name
  );
};

// QAQC Piping: Welding Data
export const useGetStreamingQaqcPipeWeldingdata = () => {
  const s = useSimpleStream<QaqcPipeWeldingdata>(
    'qaqc_pipe_weldingdata_simple',
    true,
    {
      table: 'qaqc_pipe_weldingdata',
      orderBy: undefined,
      batchSize: 25000,
      flushMs: 250,
              // persistence (minimal IO): hydrate at start, save once onDone, skip network if fresh
      persist: true,
      cacheOnce: true,
      cacheVersion: 'v1',
      gcDays: 7,
      flowControl: true,
    }
  );
  return {
    data: s.data ? { data: s.data } : undefined,
    version: (s as any).version,
    isLoading: s.isLoading,
    isError: s.isError,
    error: s.error,
    refetch: s.refetch,
  } as any;
};

// Non-streaming cached variant (previous behavior)
export const useGetQaqcPipeWeldingdata = () => {
  return useGenericFetch<QaqcPipeWeldingdata>(
    'qaqc_pipe_weldingdata',
    'qaqc_pipe_weldingdata',
    Infinity,
    undefined,
    false,
    true,
    'qaqc_pipe_weldingdata:changed',
    { persist: true, cacheOnce: true, cacheVersion: 'v1', gcDays: 7 }
  );
};

// QAQC Piping: Welding Data Result
export const useGetQaqcPipeWeldingdataResult = (enabled: boolean = false) => {
  return useGenericFetch<QaqcPipeWeldingdataResult>(
    'qaqc_pipe_weldingdata_result', // table name
    'qaqc_pipe_weldingdata_result', // query key
    Infinity,                       // never becomes stale automatically
    undefined,                      // database
    false,                          // refetch on focus (disabled)
    false,                          // event-based refresh (disabled for large dataset)
    undefined,                      // no custom event
    undefined,                      // cache options
    enabled,                        // enabled flag from consumer (Show All)
    0,                              // retryOverride: 0 (do not retry large GET)
    false,                          // refetchOnFocusOverride: false
    false,                          // refetchOnReconnectOverride: false
    'always'                        // networkModeOverride: prevent cancellation on tab hide
  );
};

// QAQC Piping: Welding Data Log (event refresh always on)
export const useGetQaqcPipeWeldingdataLog = () => {
  const s = useSimpleStream<QaqcPipeWeldingdataLog>(
    'qaqc_pipe_weldingdata_log_simple',
    true,
    {
      table: 'qaqc_pipe_weldingdata_log',
      orderBy: undefined,
      batchSize: 20000,
      flushMs: 240,
      // persistence (minimal IO): hydrate at start, save once onDone, skip network if fresh
      persist: true,
      cacheOnce: true,
      cacheVersion: 'v1',
      gcDays: 7,
      // Optional: persist small snapshots mid-stream and guard dev remounts
      incrementalPersistMs: 2000,
      devRemountGuardMs: 500,
      flowControl: true,
    }
  );

  return {
    data: s.data ? { data: s.data } : undefined,
    version: (s as any).version,
    isLoading: s.isLoading,
    isError: s.isError,
    error: s.error,
    refetch: s.refetch,
  } as any;
};

// QAQC Piping: NDT Performance view
export const useGetVwQaqcPipeNdtPerformance = () => {
  return useGenericFetch<VwQaqcPipeNdtPerformance>(
    'vw_qaqc_pipe_ndt_performance',
    'vw_qaqc_pipe_ndt_performance',
    5 * 60 * 1000 // 5 minutes
  );
};

// QAQC Piping: NDT Performance by weld kind (BW/FW/SP)
export const useGetVwQaqcPipeNdtPerformanceByKind = () => {
  return useGenericFetch<VwQaqcPipeNdtPerformanceByKind>(
    'vw_qaqc_pipe_ndt_performance_by_kind',
    'vw_qaqc_pipe_ndt_performance_by_kind',
    5 * 60 * 1000,
    undefined,
    false,
    false,
    undefined,
    { persist: true, cacheOnce: true, cacheVersion: 'v1', gcDays: 7 }
  );
};

// QAQC Piping: BC consolidated view (vw_qaqc_pipe_report_bc)
export const useGetVwQaqcPipeReportBc = () =>
  useGenericFetch<VwQaqcPipeReportBc>(
    'vw_qaqc_pipe_report_bc',
    'vw_qaqc_pipe_report_bc',
    5 * 60 * 1000
  );

export const useGetVwQaqcPipeReportVc = () =>
  useGenericFetch<VwQaqcPipeReportVc>(
    'vw_qaqc_pipe_report_vc',
    'vw_qaqc_pipe_report_vc',
    5 * 60 * 1000
  );

export const UseGetItUsers = () => {
  return useGenericFetch<ItUsers>(
     'it_users','it_users',  5 * 60 * 1000 // 10 minutes
  );
};

export const UseGetItLogsTkfAdmin = () => {
  return useGenericFetch<ItLogsTkfAdmin>(
     'it_logs_tkf_admin','it_logs_tkf_admin',  5 * 60 * 1000 // 10 minutes
  );
};

export const useGetItUserLoginLogs = () => {
  return useGenericFetch<ItUserLoginLog>(
    'it_logs_user_logins', 'it_logs_user_logins', 5 * 60 * 1000 // 10 dakika
  );
};

export const useGetDestinationFolder = () => {
  return useGenericFetch<CorDestinationFolder>(
    'cor_destination_folders', 'cor_destination_folders', 5 * 60 * 1000 // 10 dakika
  );
};

type UseVwTgfWbsHierarchyOptions = {
  params?: Record<string, any>;
  enabled?: boolean;
};
// get tgf wbs hierarchy from primavera sql server
export function useVwTkfWbsHierarchy() {
  return useGenericFetch<VwTkfWbsHierarchy>(
    'vw_TKF_wbs_hierarchy', 'vw_TKF_wbs_hierarchy', 5 * 60 * 1000, 'PPMTRAIN'
  );
}

export function useVwTgfWbsHierarchy() {
  return useGenericFetch<VwTkfWbsHierarchy>(
    'vw_TGF_wbs_hierarchy', 'vw_TGF_wbs_hierarchy', 5 * 60 * 1000, 'PPMTRAIN'
  );
}



// Fetch all records from Primavera TASK table
export const useGetPrimaveraTasks = () => {
  return useGenericFetch<PrimaveraTask>(
    'vw_TKF_tasks', // Table name in DB
    'primavera_tasks', // Query key/cache key (can be any unique string)
    5 * 60 * 1000, // Cache for 5 minutes
    'PPMTRAIN'
  );
};

export const useGetPrimaveraTasksSmall = () => {
  return useGenericFetch<PrimaveraTaskSmall>(
    'vw_TKF_tasks_small', // Table name in DB
    'primavera_tasks_small', // Query key/cache key (can be any unique string)
    5 * 60 * 1000, // Cache for 5 minutes
    'PPMTRAIN',
    false,
    false,
    undefined,
    { cacheVersion: 'v2' }
  );
};

export const useGetPrimaveraTaskSmallByCode = (task_code?: string | null) => {
  const code = task_code != null && String(task_code).trim() !== '' ? String(task_code).trim() : null;

  return useQuery<{ success?: boolean; data?: PrimaveraTaskSmall[] } | PrimaveraTaskSmall[] | any>({
    queryKey: ['vw_TKF_tasks_small', 'by_task_code', code],
    enabled: code != null,
    queryFn: async () => {
      return fetchSqlData('vw_TKF_tasks_small', 'task_code', code, 'PPMTRAIN');
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetTkfTaskResources = (task_id?: number | string | null) => {
  const tid = task_id != null && String(task_id).trim() !== '' ? Number(task_id) : null;

  return useQuery<{ success?: boolean; data?: VwTkfTaskResources[] } | VwTkfTaskResources[] | any>({
    queryKey: ['vw_TKF_task_resources', tid],
    enabled: tid != null && Number.isFinite(tid),
    queryFn: async () => {
      const sql = `
        SELECT *
        FROM dbo.vw_TKF_task_resources
        WHERE delete_date IS NULL
          AND task_id = ${Number(tid)}
        ORDER BY taskrsrc_id;
      `;
      return fetchSqlDataWithQuery(sql, 'PPMTRAIN');
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetTkfResourceSummary = () => {
  return useGenericFetch<VwTkfResourceSummary>(
    'vw_TKF_resource_summary',
    'vw_TKF_resource_summary',
    30 * 1000,
    'PPMTRAIN'
  );
};

export const useGetTkfActivityCodeTypes = () => {
  return useQuery<{ success?: boolean; data?: VwTkfTaskActivityCode[] } | VwTkfTaskActivityCode[] | any>({
    queryKey: ['vw_TKF_task_activity_codes', 'types'],
    queryFn: async () => {
      const sql = `
        SELECT DISTINCT
          actv_code_type_id,
          actv_code_type
        FROM dbo.vw_TKF_task_activity_codes
        WHERE actv_code_type_id IS NOT NULL
        ORDER BY actv_code_type;
      `;
      return fetchSqlDataWithQuery(sql, 'PPMTRAIN');
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetTkfActivityCodeValues = (actv_code_type_id?: number | string | null) => {
  const tid = actv_code_type_id != null && String(actv_code_type_id).trim() !== '' ? Number(actv_code_type_id) : null;
  return useQuery<{ success?: boolean; data?: VwTkfTaskActivityCode[] } | VwTkfTaskActivityCode[] | any>({
    queryKey: ['vw_TKF_task_activity_codes', 'values', tid],
    enabled: tid != null && Number.isFinite(tid),
    queryFn: async () => {
      const sql = `
        SELECT DISTINCT
          actv_code_id,
          actv_code,
          actv_name
        FROM dbo.vw_TKF_task_activity_codes
        WHERE actv_code_type_id = ${Number(tid)}
          AND actv_code_id IS NOT NULL
        ORDER BY actv_code;
      `;
      return fetchSqlDataWithQuery(sql, 'PPMTRAIN');
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetTkfTaskActivityCodes = () => {
  return useQuery<{ success?: boolean; data?: VwTkfTaskActivityCode[] } | VwTkfTaskActivityCode[] | any>({
    queryKey: ['vw_TKF_task_activity_codes', 'all'],
    queryFn: async () => {
      const sql = `
        SELECT
          task_id,
          actv_code_type_id,
          actv_code_type,
          actv_code_id,
          actv_code,
          actv_name
        FROM dbo.vw_TKF_task_activity_codes
        WHERE task_id IS NOT NULL
          AND actv_code_type_id IS NOT NULL
          AND actv_code_id IS NOT NULL
        ORDER BY actv_code_type, actv_code, task_id;
      `;
      return fetchSqlDataWithQuery(sql, 'PPMTRAIN');
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetTkfTaskIdsByActivityCode = (params?: {
  actv_code_type_id?: number | string | null;
  actv_code_id?: number | string | null;
}) => {
  const typeId = params?.actv_code_type_id != null && String(params.actv_code_type_id).trim() !== '' ? Number(params.actv_code_type_id) : null;
  const codeId = params?.actv_code_id != null && String(params.actv_code_id).trim() !== '' ? Number(params.actv_code_id) : null;

  return useQuery<{ success?: boolean; data?: VwTkfTaskActivityCodeTaskIdOnly[] } | VwTkfTaskActivityCodeTaskIdOnly[] | any>({
    queryKey: ['vw_TKF_task_activity_codes', 'task_ids', typeId, codeId],
    enabled: typeId != null && codeId != null && Number.isFinite(typeId) && Number.isFinite(codeId),
    queryFn: async () => {
      const sql = `
        SELECT DISTINCT task_id
        FROM dbo.vw_TKF_task_activity_codes
        WHERE actv_code_type_id = ${Number(typeId)}
          AND actv_code_id = ${Number(codeId)}
        ORDER BY task_id;
      `;
      return fetchSqlDataWithQuery(sql, 'PPMTRAIN');
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
};



export const useGetLaborTypes = () => {
  return useGenericFetch<any>(
    'cor_labor_types',    'cor_labor_types',    Infinity
  );
};

export const useGetCorFormGroup = () => {
  return useGenericFetch<CorFormGroup>(
    'cor_form_groups',    // table name
    'cor_form_groups',    // query key
    Infinity                  // staleTime
  );
};

export const useGetMatGapMaster = () => {
  return useGenericFetch<MatGapMaster>(
    'mat_gap_master',    // table name
    'mat_gap_master',    // query key
     Infinity,               // staleTime
    undefined,                 // database
    false,                     // enableRefetchOnWindowFocus
    false,                     // enableEventRefresh
    undefined,                 // eventName
    { persist: true, cacheOnce: true, cacheVersion: 'v1', gcDays: 7 } // persistence
  );
};

export const useGetMatWarehouses = () => {
  return useGenericFetch<MatWarehouses>(
    'mat_warehouses',    // table name
    'mat_warehouses',    // query key
    10 * 60 * 1000,            // staleTime
    undefined,                 // database
    true,                      // enableRefetch
    false,                     // enableEventRefresh
    undefined,                 // eventName
    { persist: true, cacheOnce: false, cacheVersion: 'v1', gcDays: 7 }, // persistence
    true,                      // enabledAtEnd
    undefined,                 // retryOverride
    undefined,                 // refetchOnFocusOverride
    undefined,                 // refetchOnReconnectOverride
    'offlineFirst',            // networkModeOverride
    'always'                   // refetchOnMountOverride
  );
};

export const useGetMatClntWarehouses = () => {
  return useGenericFetch<MatWarehouses>(
    'mat_clnt_warehouses',    // table name
    'mat_clnt_warehouses',    // query key
    10 * 60 * 1000,           // staleTime
    undefined,                // database
    true,                     // enableRefetch
    true,                     // enableEventRefresh
    'mat_clnt_warehouses:changed', // event name
    { persist: true, cacheOnce: false, cacheVersion: 'v1', gcDays: 7 }, // persistence
    true,                      // enabledAtEnd
    undefined,                 // retryOverride
    undefined,                 // refetchOnFocusOverride
    undefined,                 // refetchOnReconnectOverride
    'offlineFirst',            // networkModeOverride
    'always'                   // refetchOnMountOverride
  );
};

// Material Logistics: MPPL (mat_logistics_mppl)
export const useGetMatLogisticsMppl = () => {
  return useGenericFetch<MatLogisticsMppl>(
    'mat_logistics_mppl',
    'mat_logistics_mppl',
    10 * 60 * 1000,
    undefined,
    true,
    true,
    'mat_logistics_mppl:changed'
  );
};

export const useGetMatGapDeliveries = () => {
  return useGenericFetch<MatGapDeliveries>(
    'mat_gap_deliveries',   // table name
    'mat_gap_deliveries',   // query key
    5 * 60 * 1000           // staleTime
  );
};

export const useGetMatGapDeliveriesTemporary = () => {
  return useGenericFetch<MatGapDeliveriesTemporary>(
    'mat_gap_deliveries_temporary',   // table name
    'mat_gap_deliveries_temporary',   // query key
    5 * 60 * 1000                     // staleTime
  );
};

// Material: Client Deliveries (mat_clnt_deliveries)
export const useGetMatClntDeliveries = () => {
  return useGenericFetch<MatClntDelivery>(
    'mat_clnt_deliveries',   // table name
    'mat_clnt_deliveries',   // query key
    Infinity,          // staleTime
    undefined,               // database
    false,                   // enableRefetchOnWindowFocus
    false,                   // enableEventRefresh
    undefined,               // eventName
    { persist: true, cacheOnce: true, cacheVersion: 'v1', gcDays: 7 } // persistence
  );
};

// View: vw_mat_clnt_mat_piping_items
export const useGetVwMatClntMatPipingItems = () => {
  return useGenericFetch<VwMatClntMatPipingItem>(
    'vw_mat_clnt_mat_piping_items', // view name
    'vw_mat_clnt_mat_piping_items', // query key
    Infinity,                       // staleTime
    undefined,                      // database
    false,                          // refetch on focus
    false,                          // event refresh
    undefined,                      // eventName
    { persist: true, cacheOnce: true, cacheVersion: 'v1', gcDays: 7 }
  );
};

// Client Master materials
export const useGetMatClntMaster = () => {
  return useGenericFetch<MatClntMaster>(
    'mat_clnt_master',            // table name
    'mat_clnt_master',            // query key
    Infinity,                     // staleTime
    undefined,                    // database
    false,                        // refetch on focus
    false,                        // event-based refresh
    undefined,                    // event name
    { persist: true, cacheOnce: true, cacheVersion: 'v1', gcDays: 7 } // persistence
  );
};

// Client: Piping BOM (mat_clnt_piping_bom)
export const useGetMatClntPipingBom = () => {
  return useGenericFetch<MatClntPipingBom>(
    'mat_clnt_piping_bom',            // table name
    'mat_clnt_piping_bom',            // query key
    Infinity,                         // staleTime
    undefined,                        // database
    false,                            // refetch on focus
    false,                            // event-based refresh
    undefined,                        // event name
    { persist: true, cacheOnce: true, cacheVersion: 'v1', gcDays: 7 } // persistence
  );
};

// Transactions list
export const useGetMatGapTxns = () => {
  return useGenericFetch<MatGapTxn>(
    'mat_gap_txns',
    'mat_gap_txns',
    2 * 60 * 1000 // 2 minutes
  );
};

// On-hand per (mat_id, warehouse_id)
export const useGetMatGapWhInventory = () => {
  return useGenericFetch<VwMatGapWhInventory>(
    'vw_mat_gap_wh_inventory',
    'vw_mat_gap_wh_inventory',
    2 * 60 * 1000 // 2 minutes
  );
};

// QAQC Piping: Materials view (used by PipeMaterialSelect)
export const useGetVwQaqcPipeMaterials = () => {
  return useGenericFetch<VwQaqcPipeMaterials>(
    'vw_qaqc_pipe_materials',
    'vw_qaqc_pipe_materials',
    Infinity
  );
};

// Issue Request details (header + items enriched)
export const useGetMatGapIssueRequestDetails = () => {
  return useGenericFetch<VwMatGapIssueRequestDetails>(
    'vw_mat_gap_issue_request_details',
    'vw_mat_gap_issue_request_details',
    1 * 60 * 1000 // 1 minute
  );
};

// Issue Request headers list (cached)
export const useGetMatGapIssueRequests = () => {
  return useGenericFetch<MatGapIssueRequest>(
    'mat_gap_issue_requests',
    'mat_gap_issue_requests',
    2 * 60 * 1000 // 2 minutes stale time to prevent refetch on back navigation
  );
};

// QAQC Piping: Material Control (used by QaqcPipeMaterialControl page)
export const useGetQaqcPipeMaterialcontrol = () => {
  return useGenericFetch<any>(
    'qaqc_pipe_materialcontrol',
    'qaqc_pipe_materialcontrol',
    Infinity,
    undefined,
    false,
    true,
    'qaqc_pipe_materialcontrol:changed'
  );
};

// Outstanding per request item
export const useGetVwMatGapRequestItemOutstanding = () => {
  return useGenericFetch<VwMatGapRequestItemOutstanding>(
    'vw_mat_gap_request_item_outstanding',
    'vw_mat_gap_request_item_outstanding',
    60 * 1000 // 1 minute
  );
};

// Client: Outstanding per request item
export const useGetVwMatClntRequestItemOutstanding = () => {
  return useGenericFetch<VwMatClntRequestItemOutstanding>(
    'vw_mat_clnt_request_item_outstanding',
    'vw_mat_clnt_request_item_outstanding',
    60 * 1000 // 1 minute
  );
};

// HSE Work Permits
export const useGetHseWorkPermits = () => {
  return useGenericFetch<HseWorkPermit>(
    'hse_workpermits',
    'hse_workpermits',
    2 * 60 * 1000 // 2 minutes
  );
};

// HSE Teams
export const useGetHseTeams = () => {
  return useGenericFetch<HseTeam>(
    'hse_teams',
    'hse_teams',
    5 * 60 * 1000,
    undefined,
    false,
    true,
    'hse_teams:changed',
    { persist: true, cacheOnce: true, cacheVersion: 'v1', gcDays: 7 }
  );
};

export const useGetHseTeamMembers = () => {
  return useGenericFetch<HseTeamMember>(
    'hse_team_members',
    'hse_team_members',
    5 * 60 * 1000,
    undefined,
    false,
    true,
    'hse_team_members:changed',
    { persist: true, cacheOnce: true, cacheVersion: 'v1', gcDays: 7 }
  );
};

export const useGetVwHseTeamsFull = () => {
  return useGenericFetch<VwHseTeamsFull>(
    'vw_hse_teams_full',
    'vw_hse_teams_full',
    5 * 60 * 1000,
    undefined,
    false,
    true,
    'vw_hse_teams_full:changed',
    { persist: true, cacheOnce: true, cacheVersion: 'v1', gcDays: 7 },
    true,
    undefined,
    undefined,
    undefined,
    undefined,
    'always'
  );
};

// Machinery: const_machine_allocation list
export const useGetConstMachineAllocations = () => {
  return useGenericFetch<ConstMachineAllocation>(
    'const_machine_allocation',
    'const_machine_allocation',
    2 * 60 * 1000, // 2 minutes
    undefined,
    false,                  // refetchOnWindowFocus disabled
    true,                   // enableEventRefresh
    'const_machine_allocation:changed'
  );
};

// Machinery: vw_mach_machine_reservations view (legacy reservations list)
export const useGetMachMachineReservations = () => {
  return useGenericFetch<VwMachMachineReservation>(
    'vw_mach_machine_reservations',
    'vw_mach_machine_reservations',
    2 * 60 * 1000
  );
};

// Client: Warehouse Inventory view
export const useGetMatClntWhInventory = () => {
  return useGenericFetch<VwMatClntWhInventory>(
    'vw_mat_clnt_wh_inventory',
    'vw_mat_clnt_wh_inventory',
    2 * 60 * 1000,
    undefined,
    false,
    true,
    'vw_mat_clnt_wh_inventory:changed'
  );
};

// Client: Issue Request headers list
export const useGetMatClntIssueRequests = () => {
  return useGenericFetch<MatClntIssueRequest>(
    'mat_clnt_issue_requests',
    'mat_clnt_issue_requests',
    2 * 60 * 1000, // 2 minutes
    undefined,
    false,
    true,
    'mat_clnt_issue_requests:changed'
  );
};

// Client: Issue Request items list
export const useGetMatClntIssueRequestItems = () => {
  return useGenericFetch<MatClntIssueRequestItem>(
    'mat_clnt_issue_request_items',
    'mat_clnt_issue_request_items',
    2 * 60 * 1000, // 2 minutes
    undefined,
    false,
    true,
    'mat_clnt_issue_request_items:changed'
  );
};

// Core To-Dos (shared across modules). Filter by module on consumer side.
// Decommissioned: core todos moved to cor_tasks. Keep this block removed.


// QAQC Piping: Pipe Material Types
export const useGetPipeMaterialTypes = () => {
  return useGenericFetch<QaqcPipeMaterialTypes>(
    'qaqc_pipe_material_types',  // table name
    'qaqc_pipe_material_types',  // query key
    Infinity                  // cache indefinitely
  );
};
